document.documentElement.classList.remove('no-js');

const SHOP_ROOT = window.Shopify?.routes?.root || '/';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

function route(path) {
  return `${SHOP_ROOT}${path.replace(/^\//, '')}`;
}

function formatINR(cents) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(cents / 100);
}

function escapeHTML(value) {
  const node = document.createElement('div');
  node.textContent = value == null ? '' : String(value);
  return node.innerHTML;
}

function setupDrawer() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;
  const toggle = header.querySelector('[data-drawer-toggle]');
  const drawer = document.getElementById('SiteHeaderDrawer');
  if (!toggle || !drawer) return;

  const close = () => {
    toggle.setAttribute('aria-expanded', 'false');
    drawer.hidden = true;
    document.body.classList.remove('has-drawer-open');
  };
  toggle.addEventListener('click', () => {
    const opening = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(opening));
    drawer.hidden = !opening;
    document.body.classList.toggle('has-drawer-open', opening);
  });
  drawer.addEventListener('click', (event) => { if (event.target.closest('a')) close(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
}

function setupPredictiveSearch() {
  const panel = document.querySelector('[data-search-panel]');
  const form = panel?.querySelector('[data-predictive-search]');
  const input = form?.querySelector('[data-predictive-input]');
  const results = form?.querySelector('[data-predictive-results]');
  const status = form?.querySelector('[data-predictive-status]');
  const clear = form?.querySelector('[data-search-clear]');
  const openers = Array.from(document.querySelectorAll('[data-search-open]'));
  if (!panel || !form || !input || !results) return;

  let timer = 0;
  let controller;
  let activeIndex = -1;

  const options = () => Array.from(results.querySelectorAll('[role="option"]'));
  const setActive = (index) => {
    const items = options();
    items.forEach((item) => item.classList.remove('is-active'));
    if (!items.length) return;
    activeIndex = (index + items.length) % items.length;
    items[activeIndex].classList.add('is-active');
    items[activeIndex].scrollIntoView({ block: 'nearest' });
    input.setAttribute('aria-activedescendant', `PredictiveOption-${activeIndex}`);
    items[activeIndex].id = `PredictiveOption-${activeIndex}`;
  };
  const resetResults = () => {
    controller?.abort();
    results.hidden = true;
    results.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  };
  const open = () => {
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.getElementById('SiteHeaderDrawer')?.setAttribute('hidden', '');
    document.body.classList.remove('has-drawer-open');
    window.setTimeout(() => input.focus(), REDUCED_MOTION.matches ? 0 : 100);
  };
  const closePanel = () => {
    panel.classList.remove('is-open');
    openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    resetResults();
    window.setTimeout(() => { panel.hidden = true; }, REDUCED_MOTION.matches ? 0 : 220);
  };
  const search = async (term) => {
    controller?.abort();
    controller = new AbortController();
    status.textContent = `Searching for ${term}`;
    results.hidden = false;
    results.innerHTML = '<div class="predictive-search__loading"><span></span>Finding objects…</div>';
    try {
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'query,product,collection',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search'
      });
      const response = await fetch(`${route('search/suggest')}?${params}`, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search suggestions are temporarily unavailable.');
      const markup = await response.text();
      results.innerHTML = markup;
      const count = Number(results.querySelector('[data-result-count]')?.dataset.resultCount || 0);
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      status.textContent = count ? `${count} suggestions available` : `No suggestions for ${term}`;
    } catch (error) {
      if (error.name === 'AbortError') return;
      results.innerHTML = `<div class="predictive-search__empty"><p>${escapeHTML(error.message)}</p><span>Press Enter to search the full edit.</span></div>`;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      status.textContent = error.message;
    }
  };

  openers.forEach((button) => button.addEventListener('click', (event) => { event.preventDefault(); panel.hidden ? open() : closePanel(); }));
  panel.querySelector('[data-search-close]')?.addEventListener('click', closePanel);
  clear?.addEventListener('click', () => { input.value = ''; clear.hidden = true; resetResults(); input.focus(); });
  input.addEventListener('input', () => {
    const term = input.value.trim();
    clear.hidden = !term;
    window.clearTimeout(timer);
    if (term.length < 2) { resetResults(); status.textContent = term ? 'Type one more character for suggestions' : ''; return; }
    timer = window.setTimeout(() => search(term), 250);
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' && options().length) { event.preventDefault(); setActive(activeIndex + 1); }
    if (event.key === 'ArrowUp' && options().length) { event.preventDefault(); setActive(activeIndex - 1); }
    if (event.key === 'Enter' && activeIndex >= 0) { event.preventDefault(); options()[activeIndex]?.click(); }
    if (event.key === 'Escape') { event.preventDefault(); closePanel(); }
  });
  document.addEventListener('click', (event) => {
    if (!panel.hidden && !event.target.closest('[data-site-header]')) closePanel();
  });
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get('search') === 'open') {
    open();
    searchParams.delete('search');
    const query = searchParams.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }
}

function setupProductGalleries(scope = document) {
  scope.querySelectorAll('[data-gallery]:not([data-gallery-ready])').forEach((gallery) => {
    gallery.dataset.galleryReady = 'true';
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = track ? Array.from(track.children) : [];
    if (!track || slides.length <= 1) return;
    let index = 0;
    const setIndex = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(${-100 * index}%, 0, 0)`;
      gallery.dataset.index = String(index);
    };
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); setIndex(index - 1); });
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); setIndex(index + 1); });
    const card = gallery.closest('[data-product-card]');
    card?.addEventListener('mouseenter', () => setIndex(1));
    card?.addEventListener('mouseleave', () => setIndex(0));
  });
}

function setupMarquee() {
  if (REDUCED_MOTION.matches) return;
  document.querySelectorAll('[data-marquee-track]').forEach((track) => {
    let pos = 0;
    const tick = () => {
      pos -= 0.35;
      const width = track.firstElementChild?.getBoundingClientRect().width || 0;
      if (width && Math.abs(pos) >= width) pos = 0;
      track.style.transform = `translate3d(${pos}px, 0, 0)`;
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });
}

function setupWishlist() {
  const STORAGE_KEY = 'haus-of-her:wishlist:v1';
  const drawer = document.querySelector('[data-wishlist-drawer]');
  const itemsRoot = drawer?.querySelector('[data-wishlist-items]');
  const empty = drawer?.querySelector('[data-wishlist-empty]');
  const footer = drawer?.querySelector('[data-wishlist-footer]');
  const toast = document.querySelector('[data-wishlist-toast]');
  if (!drawer || !itemsRoot || !empty || !footer) return;

  let items = [];
  try { items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch (error) { items = []; }
  if (!Array.isArray(items)) items = [];

  const persist = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (error) { /* Private browsing may deny storage. */ }
  };
  const notify = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.add('is-visible');
    window.clearTimeout(Number(toast.dataset.timer || 0));
    toast.dataset.timer = String(window.setTimeout(() => { toast.classList.remove('is-visible'); toast.hidden = true; }, 2400));
  };
  const updateControls = () => {
    const ids = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const saved = ids.has(String(button.dataset.productId));
      button.setAttribute('aria-pressed', String(saved));
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'object'} ${saved ? 'from' : 'to'} saved items`);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((node) => { node.textContent = items.length; node.hidden = items.length === 0; });
  };
  const render = () => {
    empty.hidden = items.length > 0;
    footer.hidden = items.length === 0;
    itemsRoot.innerHTML = items.map((item) => `
      <article class="wishlist-drawer__item">
        <a href="${escapeHTML(item.url)}" class="wishlist-drawer__image">${item.image ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.title)}" loading="lazy">` : ''}</a>
        <div><p>${escapeHTML(item.vendor)}</p><a href="${escapeHTML(item.url)}">${escapeHTML(item.title)}</a><strong>${escapeHTML(item.price)}</strong><button type="button" data-wishlist-remove="${escapeHTML(item.id)}">Remove</button></div>
      </article>`).join('');
    updateControls();
  };
  const open = () => {
    render();
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('has-wishlist-open');
    drawer.querySelector('[data-wishlist-close]')?.focus();
  };
  const close = () => {
    drawer.classList.remove('is-open');
    document.body.classList.remove('has-wishlist-open');
    window.setTimeout(() => { drawer.hidden = true; }, REDUCED_MOTION.matches ? 0 : 320);
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      const id = String(toggle.dataset.productId);
      const existing = items.findIndex((item) => String(item.id) === id);
      if (existing >= 0) { const [removed] = items.splice(existing, 1); notify(`${removed.title} removed from saved items`); }
      else {
        const item = { id, handle: toggle.dataset.productHandle, url: toggle.dataset.productUrl, title: toggle.dataset.productTitle, vendor: toggle.dataset.productVendor, image: toggle.dataset.productImage, price: toggle.dataset.productPrice };
        items.unshift(item); notify(`${item.title} saved to your private edit`);
      }
      persist(); render(); return;
    }
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove) { const index = items.findIndex((item) => String(item.id) === String(remove.dataset.wishlistRemove)); if (index >= 0) items.splice(index, 1); persist(); render(); return; }
    if (event.target.closest('[data-wishlist-open]')) { event.preventDefault(); open(); return; }
    if (event.target.closest('[data-wishlist-close]')) { event.preventDefault(); close(); }
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !drawer.hidden) close(); });
  render();
}

async function loadRecommendations(container, productId, excludedIds = []) {
  if (!container || !productId) return;
  const itemsRoot = container.querySelector('[data-recommendation-items]');
  if (!itemsRoot) return;
  try {
    const url = `${route('recommendations/products')}?section_id=cart-recommendations&product_id=${encodeURIComponent(productId)}&limit=6&intent=related`;
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error();
    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const excluded = new Set(excludedIds.map(String));
    const products = Array.from(documentFragment.querySelectorAll('[data-recommendation-product-id]')).filter((product) => !excluded.has(String(product.dataset.recommendationProductId)));
    itemsRoot.replaceChildren(...products);
    container.hidden = products.length === 0;
  } catch (error) { container.hidden = true; }
}

function setupCartDrawer() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  const itemsRoot = drawer.querySelector('[data-cart-items]');
  const empty = drawer.querySelector('[data-cart-empty]');
  const footer = drawer.querySelector('[data-cart-footer]');
  const recs = drawer.querySelector('[data-cart-recommendations]');

  const open = () => {
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('has-cart-open');
    drawer.querySelector('[data-cart-close]')?.focus();
  };
  const close = () => {
    drawer.classList.remove('is-open'); document.body.classList.remove('has-cart-open');
    window.setTimeout(() => { drawer.hidden = true; }, REDUCED_MOTION.matches ? 0 : 320);
  };
  const render = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    drawer.querySelector('[data-cart-total]').textContent = formatINR(cart.total_price);
    empty.hidden = cart.item_count > 0;
    footer.hidden = cart.item_count === 0;
    itemsRoot.innerHTML = cart.items.map((item, index) => `
      <article class="cart-drawer__item">
        <a href="${escapeHTML(item.url)}"><img src="${escapeHTML(item.image || '')}" alt="${escapeHTML(item.product_title)}"></a>
        <div><p>${escapeHTML(item.vendor || '')}</p><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a><small>${escapeHTML(item.variant_title || '')}</small>
          <div class="cart-drawer__quantity"><button type="button" data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button><button type="button" data-cart-change="${index + 1}" data-quantity="0">Remove</button></div>
        </div><strong>${formatINR(item.final_line_price)}</strong>
      </article>`).join('');
    if (cart.items.length) loadRecommendations(recs, cart.items[cart.items.length - 1].product_id, cart.items.map((item) => item.product_id));
    else if (recs) recs.hidden = true;
    return cart;
  };
  const getCart = () => fetch(route('cart.js'), { headers: { Accept: 'application/json' } }).then((response) => response.json()).then(render);
  const change = (line, quantity) => fetch(route('cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) }).then((response) => response.json()).then(render);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener) { event.preventDefault(); getCart().then(open); return; }
    if (event.target.closest('[data-cart-close]')) { event.preventDefault(); close(); return; }
    const changer = event.target.closest('[data-cart-change]');
    if (changer) { changer.disabled = true; change(Number(changer.dataset.cartChange), Number(changer.dataset.quantity)).finally(() => { changer.disabled = false; }); }
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !drawer.hidden) close(); });
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    try {
      const response = await fetch(route('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error('This option could not be added.');
      await getCart(); open();
    } catch (error) { window.alert(error.message); }
    finally { if (submit) submit.disabled = false; }
  });
  getCart();
}

function setupCartPageRecommendations() {
  const container = document.querySelector('[data-cart-page-recommendations]');
  if (container) fetch(route('cart.js')).then((response) => response.json()).then((cart) => loadRecommendations(container, container.dataset.productId, cart.items.map((item) => item.product_id)));
}

function setupProductOptions() {
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const form = root.querySelector('[data-product-form]');
    const data = root.querySelector('[data-product-variants]');
    if (!form || !data) return;
    const variants = JSON.parse(data.textContent);
    const hiddenId = form.querySelector('[data-variant-id]');
    const status = form.querySelector('[data-variant-status]');
    const submit = form.querySelector('.product-form__submit');
    const prices = root.querySelectorAll('[data-live-price]');
    const comparePrices = root.querySelectorAll('[data-live-compare-price]');
    const badges = root.querySelectorAll('[data-live-sale-badge]');
    const groups = Array.from(form.querySelectorAll('[data-option-index]'));
    const selected = () => groups.map((group) => group.querySelector('input:checked')?.value);
    const update = () => {
      const values = selected();
      groups.forEach((group, index) => {
        group.querySelector('[data-option-value]').textContent = values[index] || '';
        group.querySelectorAll('input').forEach((input) => {
          const possible = variants.some((variant) => variant.available && variant.options[index] === input.value && variant.options.every((value, optionIndex) => optionIndex === index || !values[optionIndex] || value === values[optionIndex]));
          input.disabled = !possible; input.closest('.option-chip')?.classList.toggle('is-disabled', !possible);
        });
      });
      const variant = variants.find((candidate) => candidate.options.every((value, index) => value === values[index]));
      const available = Boolean(variant?.available);
      if (variant) {
        hiddenId.value = variant.id;
        prices.forEach((price) => { price.textContent = formatINR(variant.price); });
        const onSale = Number(variant.compare_at_price || 0) > Number(variant.price);
        comparePrices.forEach((price) => { price.textContent = onSale ? formatINR(variant.compare_at_price) : ''; price.hidden = !onSale; });
        badges.forEach((badge) => { badge.hidden = !onSale; });
        history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      }
      status.textContent = available ? 'Selected and available' : 'This combination is unavailable';
      submit.disabled = !available; submit.firstChild.textContent = available ? 'Add to bag ' : 'Unavailable ';
    };
    form.addEventListener('change', update); update();
  });
}

function setupFacets() {
  const closeDrawer = (drawer) => {
    drawer?.classList.remove('is-open');
    document.body.classList.remove('has-facets-open');
    drawer?.closest('[data-facets]')?.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => { if (drawer) drawer.hidden = true; }, REDUCED_MOTION.matches ? 0 : 280);
  };
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-facets-open]');
    if (opener) {
      const root = opener.closest('[data-facets]'); const drawer = root?.querySelector('[data-facets-drawer]');
      if (!drawer) return; drawer.hidden = false; requestAnimationFrame(() => drawer.classList.add('is-open')); opener.setAttribute('aria-expanded', 'true'); document.body.classList.add('has-facets-open'); return;
    }
    const closer = event.target.closest('[data-facets-close]');
    if (closer) {
      closeDrawer(closer.closest('[data-facets-drawer]'));
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer(document.querySelector('[data-facets-drawer].is-open'));
  });
  document.querySelectorAll('[data-sort-select]').forEach((select) => select.addEventListener('change', () => select.form?.requestSubmit()));
}

function setupInfiniteCatalogs() {
  document.querySelectorAll('[data-infinite-root]').forEach((root) => {
    const grid = root.querySelector('[data-product-grid]');
    const loader = root.querySelector('[data-infinite-loader]');
    const button = loader?.querySelector('[data-load-more]');
    if (!grid || !loader) return;
    let nextUrl = loader.dataset.nextUrl;
    let loading = false;
    let observer;
    const setState = (state, message) => {
      loader.dataset.state = state;
      const status = loader.querySelector('[data-infinite-status]'); if (status) status.textContent = message;
    };
    const load = async () => {
      if (loading || !nextUrl) return;
      loading = true; setState('loading', 'Loading more objects…');
      try {
        const response = await fetch(nextUrl, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error();
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incomingRoot = doc.querySelector('[data-infinite-root]');
        const incomingGrid = incomingRoot?.querySelector('[data-product-grid]');
        const incomingLoader = incomingRoot?.querySelector('[data-infinite-loader]');
        if (!incomingGrid || !incomingLoader) throw new Error();
        const existing = new Set(Array.from(grid.querySelectorAll('[data-product-id]')).map((card) => card.dataset.productId));
        let added = 0;
        Array.from(incomingGrid.children).forEach((card) => {
          const id = card.dataset.productId;
          if (!id || !existing.has(id)) { grid.appendChild(card); if (id) existing.add(id); added += 1; }
        });
        setupProductGalleries(grid);
        window.dispatchEvent(new CustomEvent('haus:products-appended', { detail: { count: added } }));
        nextUrl = incomingLoader.dataset.nextUrl;
        loader.dataset.nextUrl = nextUrl;
        const nextButton = incomingLoader.querySelector('[data-load-more]');
        if (nextUrl) {
          if (button) { button.href = nextUrl; button.hidden = false; }
          setState('idle', `${added} more objects loaded`);
        } else {
          if (button) button.hidden = true;
          setState('complete', 'You’ve reached the end of the edit'); observer?.disconnect();
        }
      } catch (error) {
        setState('error', 'More objects could not load automatically.');
        if (button) { button.hidden = false; button.textContent = 'Try load more'; }
      } finally { loading = false; }
    };
    button?.addEventListener('click', (event) => { event.preventDefault(); load(); });
    if (nextUrl && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) load(); }, { rootMargin: '600px 0px' });
      observer.observe(loader);
    } else if (!nextUrl) setState('complete', 'You’ve reached the end of the edit');
  });
}

function setupHeroCarousel() {
  document.querySelectorAll('[data-hero-carousel]').forEach((carousel) => {
    const slides = Array.from(carousel.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(carousel.querySelectorAll('[data-hero-dot]'));
    if (slides.length < 2) return;
    let index = 0;
    let timer;
    let pointer = null;
    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => { const active = slideIndex === index; slide.classList.toggle('is-active', active); slide.setAttribute('aria-hidden', String(!active)); slide.querySelectorAll('a,button').forEach((node) => node.tabIndex = active ? 0 : -1); });
      dots.forEach((dot, dotIndex) => { dot.classList.toggle('is-active', dotIndex === index); dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'); });
    };
    const autoplay = () => { window.clearInterval(timer); if (!REDUCED_MOTION.matches && !document.hidden) timer = window.setInterval(() => show(index + 1), 6500); };
    const navigate = (direction) => { show(index + direction); autoplay(); };
    carousel.querySelector('[data-hero-prev]')?.addEventListener('click', () => navigate(-1));
    carousel.querySelector('[data-hero-next]')?.addEventListener('click', () => navigate(1));
    dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.heroDot)); autoplay(); }));
    carousel.addEventListener('pointerdown', (event) => { if (event.target.closest('a,button')) return; pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null }; carousel.setPointerCapture?.(event.pointerId); });
    carousel.addEventListener('pointermove', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x; const dy = event.clientY - pointer.y;
      if (!pointer.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) pointer.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    });
    const endSwipe = (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x; const axis = pointer.axis; pointer = null;
      if (axis === 'x' && Math.abs(dx) >= 45) navigate(dx < 0 ? 1 : -1);
    };
    carousel.addEventListener('pointerup', endSwipe); carousel.addEventListener('pointercancel', () => { pointer = null; });
    carousel.addEventListener('keydown', (event) => { if (event.key === 'ArrowLeft') navigate(-1); if (event.key === 'ArrowRight') navigate(1); });
    carousel.addEventListener('mouseenter', () => window.clearInterval(timer)); carousel.addEventListener('mouseleave', autoplay);
    document.addEventListener('visibilitychange', autoplay); show(0); autoplay();
  });
}

function setupMagicCheckoutHandoff() {
  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('.cart__checkout-button[name="checkout"]');
    if (!checkout || checkout.disabled) return;
    try {
      const body = JSON.stringify({ attributes: { 'Payment preference': 'Pre-paid', 'Payment option': 'Pre-paid' } });
      if (navigator.sendBeacon) navigator.sendBeacon(route('cart/update.js'), new Blob([body], { type: 'application/json' }));
      else fetch(route('cart/update.js'), { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body });
    } catch (error) { /* Checkout must never be blocked by analytics. */ }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  setupDrawer();
  setupPredictiveSearch();
  setupProductGalleries();
  setupMarquee();
  setupWishlist();
  setupCartDrawer();
  setupCartPageRecommendations();
  setupProductOptions();
  setupFacets();
  setupInfiniteCatalogs();
  setupHeroCarousel();
  setupMagicCheckoutHandoff();
});
